/**
 * The Chat SIDEBAR state — Chat's published contract for "which chat is active
 * in the sidebar, and is the sidebar open".
 *
 * WHO OWNS IT: the app shell's nav surfaces and the projects route.
 * `nav-item.tsx` and the Evidence / Scribe / Coding nav items set
 * `chatSidebarOpenAtom` to open and close the panel; `ResizableMenuPanels` and
 * `evidence-sidebar-panel` render the session's `sidebarChatIdAtomFamily`
 * entry (through `SessionAskHeidiPane`); `pages/scribe/chat/projects` passes
 * the open state down into the split view. `ChatSidebar` owns the in-panel
 * toggle.
 *
 * WHAT CHAT DOES WITH IT: writes. Creating a chat sets the per-session
 * `sidebarChatIdAtomFamily` entry; switching also clears
 * `activeSidebarChatIdAtom`. The shell's panels follow the thread the user is
 * actually on without a `router.push` — the route change would show a
 * loading state for state that never left the client. The family is keyed by
 * session id so two sessions cannot contaminate each other's selection.
 *
 * SO THIS IS SHELL STATE CHAT WRITES, not Chat state the shell reads, which is
 * why it does not live in `@/atoms/chatAtoms`. Chat is quarantined: an import
 * of `chatAtoms.ts` from outside Chat is a coupling the migration gate counts
 * and refuses to let grow.
 * Publishing it here, outside the quarantined globs, lets the shell and Chat
 * share one identity without either side forming a pair.
 *
 * The export list is RATCHETED (`publishedContracts` in
 * `scripts/monorepo-migration/seed/program.ts`): a new export here fails
 * `pnpm migration:check` until the program owner rules it in. Panel state only
 * Chat reads is not a ruling — it belongs in `chatAtoms.ts`.
 *
 * This module imports `jotai` and `jotai-family`, and nothing else.
 * `chatContract.lean.test.ts` holds that list; an import of a
 * `@/components/chat/**` module here would drag Chat's type graph — and through
 * it the API layer — into every nav item that toggles the panel.
 *
 * No re-export is left in `chatAtoms.ts`. A re-export would keep every reader
 * resolving through the one module the Chat quarantine watches by name.
 */

import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

export const chatSidebarOpenAtom = atom<boolean>(false);

// Only the transcript-chip query sets a chat here and nothing clears it on
// navigation, so the in-session pane ignores it. The floating bar's and note
// overlay's history dropdowns still pass it as `currentChatId`.
export const activeSidebarChatIdAtom = atom<string | null>(null);

export const sidebarChatIdAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_sessionId: string) => atom<string | null>(null)
);
